import { HttpClient } from '@angular/common/http';
import { inject, Injectable, NgZone, signal } from '@angular/core';
import { Router } from '@angular/router';
import { finalize, Observable, shareReplay, tap } from 'rxjs';
import { GATEWAY_URL } from './api.config';
import { RefreshableSession, SIGNED_IN_ELSEWHERE_STATE, withSession } from './auth-session';
import { browserId } from './browser-id';
import { DEFAULT_SESSION_TIMEOUT_MINUTES, IdleTimer, SessionSettings } from './idle-timer';
import { PublicInvited, SessionConflict, TokenPair, TwoFactorSetup } from './models';
import { SESSION_SOCKET_FACTORY, SessionSocket } from './session-socket';

/**
 * The invited-user session — deliberately SEPARATE from the admin session
 * (its own storage keys), so a browser can hold an admin session and an
 * invited session independently. Authenticated calls declare this session
 * (`withSession('invited')`); the shared interceptor attaches the invited token,
 * refreshes it when it expires, and ends the session when the refresh token is
 * dead. Inactivity ends it too, after the user's session timeout — the same
 * behaviour as the admin portal.
 */
const TOKENS_KEY = 'auth_m_invited_tokens';
const USER_KEY = 'auth_m_invited_user';

export interface InvitedSignInResponse {
  status: '2fa_required' | 'ok' | 'session_conflict';
  method?: string;
  challenge_token?: string;
  sms_available?: boolean;
  authenticator_available?: boolean;
  user?: PublicInvited;
  tokens?: TokenPair;
  session_conflict?: SessionConflict;
}

/** 2FA verify and signin/confirm: signed in, or stopped at a session conflict. */
export interface InvitedAuthResponse {
  status: 'ok' | 'session_conflict';
  user?: PublicInvited;
  tokens?: TokenPair;
  session_conflict?: SessionConflict;
}

@Injectable({ providedIn: 'root' })
export class InvitedAuthService implements RefreshableSession {
  readonly user = signal<PublicInvited | null>(readJson<PublicInvited>(USER_KEY));

  private readonly idle: IdleTimer;
  private readonly socket: SessionSocket;
  private refreshInFlight$: Observable<TokenPair> | null = null;

  constructor(private http: HttpClient, private router: Router, zone: NgZone) {
    // Inactivity ends the session and returns the user to the login page —
    // the same behaviour the admin portal has.
    this.idle = new IdleTimer(zone, () => this.timedOut());
    // A sign-in from another browser or device ends the session at once.
    this.socket = new SessionSocket(zone, inject(SESSION_SOCKET_FACTORY),
      () => this.tokens?.access_token, () => this.refresh(), () => this.sessionRevoked());
    // A session restored from storage (a page reload) is watched from the
    // start. Deferred because the settings request passes through the
    // interceptor, which injects this service — construction must finish first.
    if (this.isLoggedIn) {
      queueMicrotask(() => {
        this.startIdleWatch();
        this.socket.connect();
      });
    }
  }

  private base = `${GATEWAY_URL}/auth/invited`;

  // ── login ─────────────────────────────────────────────────────────────
  signIn(email: string, password: string): Observable<InvitedSignInResponse> {
    return this.http
      .post<InvitedSignInResponse>(`${this.base}/signin`, { email, password, browser_id: browserId() })
      .pipe(tap((res) => this.storeIfSignedIn(res)));
  }

  verifyPattern(email: string, pattern: string[]): Observable<unknown> {
    return this.http.post(`${this.base}/pattern/verify`, { email, pattern });
  }

  verify2fa(challengeToken: string, code: string): Observable<InvitedAuthResponse> {
    return this.http
      .post<InvitedAuthResponse>(`${this.base}/2fa/verify`, { challenge_token: challengeToken, code })
      .pipe(tap((res) => this.storeIfSignedIn(res)));
  }

  /** The user agreed to sign out their other browser or device: finish signing in. */
  confirmSignIn(confirmToken: string): Observable<InvitedAuthResponse> {
    return this.http
      .post<InvitedAuthResponse>(`${this.base}/signin/confirm`, { confirm_token: confirmToken })
      .pipe(tap((res) => this.storeIfSignedIn(res)));
  }

  resend2fa(challengeToken: string): Observable<unknown> {
    return this.http.post(`${this.base}/2fa/resend`, { challenge_token: challengeToken });
  }

  // ── set-password (with phone verification) ────────────────────────────
  // The invited user enters their own phone here; it's registered + the code
  // is sent to it.
  sendSetPasswordPhoneCode(token: string, phone: string): Observable<unknown> {
    return this.http.post(`${this.base}/set-password/send-phone-code`, { token, phone });
  }

  verifySetPasswordPhone(token: string, code: string): Observable<unknown> {
    return this.http.post(`${this.base}/set-password/verify-phone`, { token, code });
  }

  setPassword(token: string, password: string): Observable<unknown> {
    return this.http.post(`${this.base}/set-password`, { token, password });
  }

  // Final onboarding step: set the mandatory pattern → activates the account.
  setOnboardingPattern(token: string, pattern: string[]): Observable<unknown> {
    return this.http.post(`${this.base}/set-password/pattern`, { token, pattern });
  }

  checkSetPasswordToken(token: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.base}/set-password/check`, { token });
  }

  resendSetPassword(token: string): Observable<unknown> {
    return this.http.post(`${this.base}/set-password/resend`, { token });
  }

  // ── forgot security pattern ───────────────────────────────────────────
  forgotPattern(email: string): Observable<unknown> {
    return this.http.post(`${this.base}/forgot-pattern`, { email });
  }

  resetPatternSendCode(token: string): Observable<unknown> {
    return this.http.post(`${this.base}/reset-pattern/send-phone-code`, { token });
  }

  resetPatternVerifyPhone(token: string, code: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(`${this.base}/reset-pattern/verify-phone`, { token, code });
  }

  resetPattern(proofToken: string, pattern: string[]): Observable<unknown> {
    return this.http.post(`${this.base}/reset-pattern`, { proof_token: proofToken, pattern });
  }

  // ── passwords ─────────────────────────────────────────────────────────
  forgotPassword(email: string): Observable<unknown> {
    return this.http.post(`${this.base}/forgot-password`, { email });
  }

  // Reset-password now verifies the registered phone first (send → verify →
  // proof → reset).
  resetPasswordSendCode(token: string): Observable<unknown> {
    return this.http.post(`${this.base}/reset-password/send-phone-code`, { token });
  }

  resetPasswordVerifyPhone(token: string, code: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(`${this.base}/reset-password/verify-phone`, { token, code });
  }

  resetPassword(proofToken: string, newPassword: string): Observable<unknown> {
    return this.http.post(`${this.base}/reset-password`, { proof_token: proofToken, new_password: newPassword });
  }

  // ── self-service lock recovery ────────────────────────────────────────
  recoverSendPhoneCode(token: string): Observable<unknown> {
    return this.http.post(`${this.base}/recover/send-phone-code`, { token });
  }

  recoverVerifyPhone(token: string, code: string): Observable<unknown> {
    return this.http.post(`${this.base}/recover/verify-phone`, { token, code });
  }

  // Recovery already verified the phone, so complete returns a password-reset
  // proof directly (used with resetPassword).
  recoverComplete(token: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(`${this.base}/recover/complete`, { token });
  }

  // ── authenticated (invited token attached explicitly) ─────────────────
  me(): Observable<PublicInvited> {
    return this.http
      .get<PublicInvited>(`${this.base}/me`, { context: withSession('invited') })
      .pipe(tap((u) => this.storeUser(u)));
  }

  changePassword(currentPassword: string, newPassword: string): Observable<unknown> {
    return this.http.post(
      `${this.base}/change-password`,
      { current_password: currentPassword, new_password: newPassword },
      { context: withSession('invited') },
    );
  }

  // ── changing your email address or phone number ─────────────────────
  //
  // The same flows as the admin portal. Both take two codes, entered here on
  // the Security page — the first proves the request, the second applies it:
  //
  //   email: code -> the REGISTERED phone; then the new address, and a code to it
  //   phone: code -> the NEW phone;        then a code to the REGISTERED address
  //
  // The second code is entered on the Security page and applies the change.

  /** Email, step 1: texts a code to the REGISTERED phone — before any new address is asked for. */
  startEmailChange(): Observable<{ status: string; phone_hint: string }> {
    return this.http.post<{ status: string; phone_hint: string }>(
      `${this.base}/me/email-change`,
      {},
      { context: withSession('invited') },
    );
  }

  /** Email, step 2: verify that code. The new address is only asked for after this. */
  verifyEmailChangeCode(code: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${this.base}/me/email-change/verify-code`,
      { code },
      { context: withSession('invited') },
    );
  }

  /** Email, step 3: the new address — a confirmation code is emailed to it. */
  submitNewEmail(newEmail: string): Observable<{ status: string; new_email: string }> {
    return this.http.post<{ status: string; new_email: string }>(
      `${this.base}/me/email-change/new-email`,
      { new_email: newEmail },
      { context: withSession('invited') },
    );
  }

  /** Phone, step 1: texts a code to the NEW number. */
  startPhoneChange(newPhone: string): Observable<{ status: string; phone_hint: string }> {
    return this.http.post<{ status: string; phone_hint: string }>(
      `${this.base}/me/phone-change`,
      { new_phone: newPhone },
      { context: withSession('invited') },
    );
  }

  /** Phone, step 2: the code checks out, so a code goes to the REGISTERED address. */
  verifyPhoneChangeCode(code: string): Observable<{ status: string; email_hint: string }> {
    return this.http.post<{ status: string; email_hint: string }>(
      `${this.base}/me/phone-change/verify-code`,
      { code },
      { context: withSession('invited') },
    );
  }

  /** Email, step 4: the code from the NEW address — applies the change. */
  confirmEmailChange(code: string): Observable<{ status: string; email: string }> {
    return this.http.post<{ status: string; email: string }>(
      `${this.base}/me/email-change/confirm`,
      { code },
      { context: withSession('invited') },
    );
  }

  /** Phone, step 3: the code from the REGISTERED address — applies the change. */
  confirmPhoneChange(code: string): Observable<{ status: string; phone: string }> {
    return this.http.post<{ status: string; phone: string }>(
      `${this.base}/me/phone-change/confirm`,
      { code },
      { context: withSession('invited') },
    );
  }

  /** Sets a FIRST pattern. Replacing one is phone-verified (below); a pattern can't be removed. */
  setSecurityPattern(pattern: string[]): Observable<unknown> {
    return this.http.post(`${this.base}/security-pattern`, { pattern }, { context: withSession('invited') });
  }

  /** Pattern change, step 1: a code to the REGISTERED phone. */
  sendPatternCode(): Observable<{ status: string; phone_hint: string }> {
    return this.http.post<{ status: string; phone_hint: string }>(
      `${this.base}/security-pattern/send-phone-code`,
      {},
      { context: withSession('invited') },
    );
  }

  /** Pattern change, step 2: the code checks out → a short-lived proof. */
  verifyPatternChangeCode(code: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(
      `${this.base}/security-pattern/verify-phone`,
      { code },
      { context: withSession('invited') },
    );
  }

  /** Pattern change, step 3: save the new pattern with that proof. */
  changeSecurityPattern(proofToken: string, pattern: string[]): Observable<unknown> {
    return this.http.post(
      `${this.base}/security-pattern/change`,
      { proof_token: proofToken, pattern },
      { context: withSession('invited') },
    );
  }

  // ── 2FA method management ─────────────────────────────────────────────
  setupAuthenticator(currentPassword: string): Observable<TwoFactorSetup> {
    return this.http.post<TwoFactorSetup>(
      `${this.base}/2fa/setup`,
      { current_password: currentPassword },
      { context: withSession('invited') },
    );
  }

  confirmAuthenticator(code: string): Observable<unknown> {
    return this.http.post(`${this.base}/2fa/confirm`, { code }, { context: withSession('invited') });
  }

  removeAuthenticator(currentPassword: string): Observable<unknown> {
    return this.http.post(`${this.base}/2fa/remove`, { current_password: currentPassword }, { context: withSession('invited') });
  }

  setDefaultMethod(method: 'email' | 'sms' | 'totp'): Observable<unknown> {
    return this.http.post(`${this.base}/2fa/method`, { method }, { context: withSession('invited') });
  }

  select2fa(challengeToken: string, method: 'email' | 'sms' | 'totp'): Observable<unknown> {
    return this.http.post(`${this.base}/2fa/select`, { challenge_token: challengeToken, method });
  }

  // ── session ───────────────────────────────────────────────────────────
  get tokens(): TokenPair | null {
    return readJson<TokenPair>(TOKENS_KEY);
  }

  get isLoggedIn(): boolean {
    return !!this.tokens;
  }

  logout(): void {
    this.endSession();
    this.router.navigate(['/invited/login']);
  }

  /**
   * Single-flight: refresh tokens are single-use (rotated on the server), so
   * concurrent 401s must share ONE refresh — the same rule as the admin session.
   */
  refresh(): Observable<TokenPair> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }
    const refreshToken = this.tokens?.refresh_token ?? '';
    this.refreshInFlight$ = this.http
      .post<TokenPair>(
        `${GATEWAY_URL}/auth/refresh`,
        { refresh_token: refreshToken },
        { context: withSession('invited') },
      )
      .pipe(
        tap((pair) => sessionStorage.setItem(TOKENS_KEY, JSON.stringify(pair))),
        shareReplay({ bufferSize: 1, refCount: false }),
        finalize(() => (this.refreshInFlight$ = null)),
      );
    return this.refreshInFlight$;
  }

  /**
   * Called by the interceptor when the refresh token itself is dead (expired,
   * or revoked — e.g. by a password change): the session is over, so leave a
   * page that can no longer load anything and sign in again.
   */
  sessionExpired(): void {
    this.idle.stop();
    this.clearSession();
    this.router.navigate(['/invited/login']);
  }

  /**
   * A sign-in from another browser or device replaced this session (heard on
   * the socket, or from a 401 "session_revoked"): end it here and say why.
   */
  sessionRevoked(): void {
    if (!this.isLoggedIn) return;
    this.idle.stop();
    this.clearSession();
    this.router.navigate(['/invited/login'], { state: SIGNED_IN_ELSEWHERE_STATE });
  }

  // ── session timeout ─────────────────────────────────────────────────────
  settings(): Observable<SessionSettings> {
    return this.http.get<SessionSettings>(`${this.base}/me/settings`, { context: withSession('invited') });
  }

  saveSettings(settings: SessionSettings): Observable<SessionSettings> {
    return this.http.put<SessionSettings>(`${this.base}/me/settings`, settings, {
      context: withSession('invited'),
    });
  }

  /** Apply a newly saved timeout to the running watch straight away. */
  applyTimeoutMinutes(minutes: number): void {
    if (this.isLoggedIn) {
      this.idle.watch(minutes);
    }
  }

  private startIdleWatch(): void {
    if (!this.isLoggedIn) return;
    this.settings().subscribe({
      next: (s) => this.isLoggedIn && this.idle.watch(s.session_timeout_minutes),
      error: () => this.isLoggedIn && this.idle.watch(DEFAULT_SESSION_TIMEOUT_MINUTES),
    });
  }

  /** The inactivity timeout elapsed: end the session like a sign-out, then log in again. */
  private timedOut(): void {
    this.endSession();
    this.router.navigate(['/invited/login']);
  }

  /** Revoke the refresh token server-side and forget the session locally. */
  private endSession(): void {
    this.idle.stop();
    const refreshToken = this.tokens?.refresh_token;
    if (refreshToken) {
      this.http
        .post(`${GATEWAY_URL}/auth/logout`, { refresh_token: refreshToken }, { context: withSession('invited') })
        .subscribe({ error: () => undefined });
    }
    this.clearSession();
  }

  private clearSession(): void {
    this.socket.disconnect();
    sessionStorage.removeItem(TOKENS_KEY);
    sessionStorage.removeItem(USER_KEY);
    this.user.set(null);
  }

  /** A session conflict (or a pending 2FA step) carries no tokens yet. */
  private storeIfSignedIn(res: { status: string; user?: PublicInvited; tokens?: TokenPair }): void {
    if (res.status === 'ok' && res.user && res.tokens) {
      this.storeSession(res.user, res.tokens);
    }
  }

  private storeSession(user: PublicInvited, tokens: TokenPair): void {
    sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
    this.storeUser(user);
    this.startIdleWatch();
    this.socket.connect();
  }

  private storeUser(user: PublicInvited): void {
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    this.user.set(user);
  }
}

function readJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
